'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdEdit,
    MdDescription,
    MdPerson,
    MdLocalHospital,
    MdCode,
    MdSend,
    MdCheckCircle,
    MdCancel,
    MdHistory,
    MdAdd,
    MdDelete,
} from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    getGuide,
    getGuideHistory,
    submitGuideForAuthorization,
    authorizeGuide,
    submitGuide,
    cancelGuide,
    removeGuideItem,
    TissGuide,
    TissGuideHistory,
    GUIDE_STATUS_LABELS,
    GUIDE_STATUS_COLORS,
    GUIDE_TYPE_LABELS,
    GUIDE_ORIGIN_LABELS,
} from '@/lib/api/billing/guide-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import ConfirmModal from '@/components/confirm-modal'

export default function GuideDetailPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('billing', 'guides')

    const [guide, setGuide] = useState<TissGuide | null>(null)
    const [history, setHistory] = useState<TissGuideHistory[]>([])
    const [loading, setLoading] = useState(true)
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [showAuthorizeModal, setShowAuthorizeModal] = useState(false)
    const [cancelReason, setCancelReason] = useState('')
    const [showRemoveItemModal, setShowRemoveItemModal] = useState(false)
    const [itemToRemove, setItemToRemove] = useState<number | null>(null)
    const [authorizeData, setAuthorizeData] = useState({
        authorizationNumber: '',
        operatorNumber: '',
        validUntil: '',
    })
    const [activeTab, setActiveTab] = useState<'items' | 'history'>('items')

    useEffect(() => {
        if (id && user?.companyId) {
            loadGuide()
            loadHistory()
        }
    }, [id, user?.companyId])

    const loadGuide = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getGuide(parseInt(id, 10))
            if (response.success && response.result) {
                setGuide(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar guia')
                navigate('/billing/guides')
            }
        } catch (error) {
            toast.error('Erro ao carregar guia')
            navigate('/billing/guides')
        } finally {
            setLoading(false)
        }
    }

    const loadHistory = async () => {
        if (!id) return

        try {
            const response = await getGuideHistory(parseInt(id, 10))
            if (response.success) {
                setHistory(response.result || [])
            }
        } catch (error) {
            console.error('Erro ao carregar histórico:', error)
        }
    }

    const handleSubmitForAuthorization = async () => {
        if (!id) return

        try {
            const response = await submitGuideForAuthorization(parseInt(id, 10))
            if (response.success) {
                toast.success('Guia enviada para autorização')
                loadGuide()
                loadHistory()
            } else {
                toast.error(response.error || 'Erro ao enviar guia')
            }
        } catch (error) {
            toast.error('Erro ao enviar guia')
        }
    }

    const handleAuthorize = async () => {
        if (!id) return

        try {
            const response = await authorizeGuide(parseInt(id, 10), {
                authorizationNumber:
                    authorizeData.authorizationNumber || undefined,
                operatorNumber: authorizeData.operatorNumber || undefined,
                validUntil: authorizeData.validUntil || undefined,
            })
            if (response.success) {
                toast.success('Guia autorizada com sucesso')
                setShowAuthorizeModal(false)
                loadGuide()
                loadHistory()
            } else {
                toast.error(response.error || 'Erro ao autorizar guia')
            }
        } catch (error) {
            toast.error('Erro ao autorizar guia')
        }
    }

    const handleSubmit = async () => {
        if (!id) return

        try {
            const response = await submitGuide(parseInt(id, 10))
            if (response.success) {
                toast.success('Guia enviada com sucesso')
                loadGuide()
                loadHistory()
            } else {
                toast.error(response.error || 'Erro ao enviar guia')
            }
        } catch (error) {
            toast.error('Erro ao enviar guia')
        }
    }

    const handleCancel = async () => {
        if (!id) return

        try {
            const response = await cancelGuide(
                parseInt(id, 10),
                cancelReason || undefined
            )
            if (response.success) {
                toast.success('Guia cancelada')
                setShowCancelModal(false)
                setCancelReason('')
                loadGuide()
                loadHistory()
            } else {
                toast.error(response.error || 'Erro ao cancelar guia')
            }
        } catch (error) {
            toast.error('Erro ao cancelar guia')
        }
    }

    const handleRemoveItem = (itemId: number) => {
        setItemToRemove(itemId)
        setShowRemoveItemModal(true)
    }

    const confirmRemoveItem = async () => {
        if (!id || !itemToRemove) return

        try {
            const response = await removeGuideItem(parseInt(id, 10), itemToRemove)
            if (response.success) {
                toast.success('Item removido')
                loadGuide()
            } else {
                toast.error(response.error || 'Erro ao remover item')
            }
        } catch (error) {
            toast.error('Erro ao remover item')
        } finally {
            setShowRemoveItemModal(false)
            setItemToRemove(null)
        }
    }

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value)
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando guia...
                </p>
            </div>
        )
    }

    if (!guide) {
        return null
    }

    const canSubmitForAuth = guide.status === 'GUIDE_DRAFT'
    const canAuthorize = guide.status === 'GUIDE_PENDING_AUTHORIZATION'
    const canSubmit = guide.status === 'GUIDE_AUTHORIZED'
    const canCancelGuide = !['GUIDE_CANCELLED', 'GUIDE_PAID'].includes(
        guide.status
    )
    const canEditGuide = guide.status === 'GUIDE_DRAFT'

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/billing/guides')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                        <MdDescription className="text-2xl text-white" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                                Guia {guide.guideNumber}
                            </h1>
                            <span
                                className={`badge ${GUIDE_STATUS_COLORS[guide.status]}`}
                            >
                                {GUIDE_STATUS_LABELS[guide.status]}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="badge badge-ghost badge-sm">
                                {GUIDE_TYPE_LABELS[guide.guideType]}
                            </span>
                            <span className="badge badge-ghost badge-sm">
                                {GUIDE_ORIGIN_LABELS[guide.origin]}
                            </span>
                        </div>
                    </div>
                </div>
                {(canUpdate || canDelete) && (
                    <div className="flex items-center gap-2 flex-wrap">
                        {canUpdate && canSubmitForAuth && (
                            <button
                                onClick={handleSubmitForAuthorization}
                                className="btn btn-info btn-sm gap-2"
                            >
                                <MdSend className="text-lg" />
                                Enviar p/ Autorização
                            </button>
                        )}
                        {canUpdate && canAuthorize && (
                            <button
                                onClick={() => setShowAuthorizeModal(true)}
                                className="btn btn-success btn-sm gap-2"
                            >
                                <MdCheckCircle className="text-lg" />
                                Autorizar
                            </button>
                        )}
                        {canUpdate && canSubmit && (
                            <button
                                onClick={handleSubmit}
                                className="btn btn-secondary btn-sm gap-2"
                            >
                                <MdSend className="text-lg" />
                                Enviar
                            </button>
                        )}
                        {canUpdate && canEditGuide && (
                            <button
                                onClick={() =>
                                    navigate(`/billing/guides/${id}/edit`)
                                }
                                className="btn btn-secondary btn-sm gap-2"
                            >
                                <MdEdit className="text-lg" />
                                Editar
                            </button>
                        )}
                        {canDelete && canCancelGuide && (
                            <button
                                onClick={() => setShowCancelModal(true)}
                                className="btn btn-error btn-sm gap-2"
                            >
                                <MdCancel className="text-lg" />
                                Cancelar
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <MdPerson className="text-secondary" />
                            <span className="text-xs text-base-content/60 uppercase">
                                Paciente
                            </span>
                        </div>
                        <p className="font-semibold">
                            {guide.client?.name || '-'}
                        </p>
                    </div>
                </div>
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <MdLocalHospital className="text-secondary" />
                            <span className="text-xs text-base-content/60 uppercase">
                                Convenio
                            </span>
                        </div>
                        <p className="font-semibold">
                            {guide.healthPlan?.name || '-'}
                        </p>
                    </div>
                </div>
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <MdCode className="text-secondary" />
                            <span className="text-xs text-base-content/60 uppercase">
                                Autorização
                            </span>
                        </div>
                        <p className="font-semibold">
                            {guide.authorizationNumber || '-'}
                        </p>
                    </div>
                </div>
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <span className="text-xs text-base-content/60 uppercase">
                            Valor Total
                        </span>
                        <p className="text-xl font-bold text-secondary">
                            {formatCurrency(guide.totalRequested)}
                        </p>
                        {guide.totalAuthorized !== undefined &&
                            guide.totalAuthorized !== guide.totalRequested && (
                                <p className="text-sm text-success">
                                    Autorizado:{' '}
                                    {formatCurrency(guide.totalAuthorized)}
                                </p>
                            )}
                    </div>
                </div>
            </div>

            {/* Dates Info */}
            <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                <div className="card-body">
                    <h2 className="card-title text-lg mb-4">Datas</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                            <p className="text-xs text-base-content/60 uppercase">
                                Solicitação
                            </p>
                            <p className="font-semibold">
                                {guide.requestDate
                                    ? format(
                                        new Date(guide.requestDate),
                                        'dd/MM/yyyy',
                                        { locale: ptBR }
                                    )
                                    : '-'}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-base-content/60 uppercase">
                                Autorização
                            </p>
                            <p className="font-semibold">
                                {guide.authorizationDate
                                    ? format(
                                        new Date(guide.authorizationDate),
                                        'dd/MM/yyyy',
                                        { locale: ptBR }
                                    )
                                    : '-'}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-base-content/60 uppercase">
                                Execução
                            </p>
                            <p className="font-semibold">
                                {guide.executionDate
                                    ? format(
                                        new Date(guide.executionDate),
                                        'dd/MM/yyyy',
                                        { locale: ptBR }
                                    )
                                    : '-'}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-base-content/60 uppercase">
                                Validade
                            </p>
                            <p className="font-semibold">
                                {guide.validUntil
                                    ? format(
                                        new Date(guide.validUntil),
                                        'dd/MM/yyyy',
                                        { locale: ptBR }
                                    )
                                    : '-'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="tabs tabs-boxed w-fit">
                <button
                    onClick={() => setActiveTab('items')}
                    className={`tab ${activeTab === 'items' ? 'tab-active' : ''}`}
                >
                    Itens ({guide.items?.length || 0})
                </button>
                <button
                    onClick={() => setActiveTab('history')}
                    className={`tab ${activeTab === 'history' ? 'tab-active' : ''}`}
                >
                    <MdHistory className="mr-1" />
                    Histórico
                </button>
            </div>

            {/* Items Tab */}
            {activeTab === 'items' && (
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="card-title text-lg">
                                Itens da Guia
                            </h2>
                            {canCreate && canEditGuide && (
                                <button
                                    onClick={() =>
                                        navigate(
                                            `/billing/guides/${id}/itens/add`
                                        )
                                    }
                                    className="btn btn-secondary btn-sm gap-2"
                                >
                                    <MdAdd className="text-lg" />
                                    Adicionar
                                </button>
                            )}
                        </div>

                        {!guide.items || guide.items.length === 0 ? (
                            <div className="text-center py-8 text-base-content/60">
                                <p>Nenhum item na guia...</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Código</th>
                                            <th>Procedimento</th>
                                            <th className="text-center">Qtd</th>
                                            <th className="text-right">
                                                Valor Unit.
                                            </th>
                                            <th className="text-right">Total</th>
                                            {canDelete && canEditGuide && <th></th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {guide.items.map(item => (
                                            <tr key={item.id}>
                                                <td className="font-mono text-sm">
                                                    {item.procedureCode}
                                                </td>
                                                <td>{item.procedureName}</td>
                                                <td className="text-center">
                                                    {item.quantity}
                                                </td>
                                                <td className="text-right">
                                                    {formatCurrency(
                                                        item.unitValue
                                                    )}
                                                </td>
                                                <td className="text-right font-semibold">
                                                    {formatCurrency(
                                                        item.totalValue
                                                    )}
                                                </td>
                                                {canDelete && canEditGuide && (
                                                    <td>
                                                        <button
                                                            onClick={() =>
                                                                handleRemoveItem(
                                                                    item.id
                                                                )
                                                            }
                                                            className="btn btn-ghost btn-xs text-error"
                                                        >
                                                            <MdDelete className="text-lg" />
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td
                                                colSpan={
                                                    canDelete && canEditGuide
                                                        ? 4
                                                        : 3
                                                }
                                                className="text-right font-semibold"
                                            >
                                                Total:
                                            </td>
                                            <td className="text-right font-bold text-secondary">
                                                {formatCurrency(
                                                    guide.totalRequested
                                                )}
                                            </td>
                                            {canDelete && canEditGuide && <td></td>}
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">Histórico</h2>

                        {history.length === 0 ? (
                            <div className="text-center py-8 text-base-content/60">
                                <p>Nenhum registro no histórico</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {history.map(entry => (
                                    <div
                                        key={entry.id}
                                        className="flex gap-4 pb-4 border-b border-base-300/50 last:border-0"
                                    >
                                        <div className="w-2 h-2 rounded-full bg-secondary mt-2 flex-shrink-0"></div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="font-semibold">
                                                    {entry.action}
                                                </span>
                                                {entry.newStatus && (
                                                    <span className="badge badge-sm badge-ghost">
                                                        {
                                                            GUIDE_STATUS_LABELS[
                                                            entry.newStatus as keyof typeof GUIDE_STATUS_LABELS
                                                            ]
                                                        }
                                                    </span>
                                                )}
                                            </div>
                                            {entry.notes && (
                                                <p className="text-sm text-base-content/70 mb-1">
                                                    {entry.notes}
                                                </p>
                                            )}
                                            <p className="text-xs text-base-content/50">
                                                {entry.user?.name || 'Sistema'}{' '}
                                                -{' '}
                                                {format(
                                                    new Date(entry.createdAt),
                                                    "dd/MM/yyyy 'as' HH:mm",
                                                    { locale: ptBR }
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Cancel Modal */}
            {showCancelModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">Cancelar Guia</h3>
                        <div className="py-4">
                            <label className="label">
                                <span className="label-text">
                                    Motivo do cancelamento
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                value={cancelReason}
                                onChange={e => setCancelReason(e.target.value)}
                                placeholder="Informe o motivo (opcional)"
                                rows={3}
                            />
                        </div>
                        <div className="modal-action">
                            <button
                                onClick={() => {
                                    setShowCancelModal(false)
                                    setCancelReason('')
                                }}
                                className="btn btn-ghost"
                            >
                                Voltar
                            </button>
                            <button onClick={handleCancel} className="btn btn-error">
                                Confirmar Cancelamento
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setShowCancelModal(false)}
                    ></div>
                </div>
            )}

            {/* Authorize Modal */}
            {showAuthorizeModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">Autorizar Guia</h3>
                        <div className="py-4 space-y-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">
                                        Número da Autorização
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    value={authorizeData.authorizationNumber}
                                    onChange={e =>
                                        setAuthorizeData(prev => ({
                                            ...prev,
                                            authorizationNumber: e.target.value,
                                        }))
                                    }
                                    placeholder="Número da autorização"
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">
                                        Número da Operadora
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    value={authorizeData.operatorNumber}
                                    onChange={e =>
                                        setAuthorizeData(prev => ({
                                            ...prev,
                                            operatorNumber: e.target.value,
                                        }))
                                    }
                                    placeholder="Número na operadora"
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">
                                        Validade da Autorização
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={authorizeData.validUntil}
                                    onChange={e =>
                                        setAuthorizeData(prev => ({
                                            ...prev,
                                            validUntil: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                        <div className="modal-action">
                            <button
                                onClick={() => setShowAuthorizeModal(false)}
                                className="btn btn-ghost"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleAuthorize}
                                className="btn btn-success"
                            >
                                Autorizar
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setShowAuthorizeModal(false)}
                    ></div>
                </div>
            )}

            <ConfirmModal
                isOpen={showRemoveItemModal}
                onClose={() => {
                    setShowRemoveItemModal(false)
                    setItemToRemove(null)
                }}
                onConfirm={confirmRemoveItem}
                title="Remover item"
                message="Remover este item da guia?"
                confirmButtonText="Remover"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
